import { ApiError, createApiErrorFromResponse } from "../api";

type StrictTokenReadConfig = {
  fieldName: string;
  invalidCode: string;
  invalidMessage: string;
};

export function readOptionalStringToken(value: unknown, config: StrictTokenReadConfig) {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value !== "string") {
    throw new ApiError(500, {
      code: config.invalidCode,
      message: config.invalidMessage,
      details: {
        field: config.fieldName,
        received_type: Array.isArray(value) ? "array" : typeof value,
      },
    });
  }
  const trimmed = value.trim();
  return trimmed || null;
}

export function isUnauthorizedApiError(error: unknown) {
  return error instanceof ApiError && error.status === 401;
}

export async function readApiErrorDetail(response: Response) {
  const text = await response.text();
  if (!text) {
    return response.statusText || "Request failed";
  }
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  return text;
}

export async function parseJsonResponse<T>(response: Response): Promise<T> {
  const createEmptyJsonError = () =>
    createApiErrorFromResponse(response, {
      code: "platform.api.json_response_empty",
      message:
        response.status === 204
          ? "Expected JSON response body but received 204 No Content."
          : "Expected JSON response body but received an empty response body.",
    });

  if (response.status === 204) {
    throw createEmptyJsonError();
  }
  const text = await response.text();
  if (!text) {
    throw createEmptyJsonError();
  }
  return JSON.parse(text) as T;
}

export function isOidcInvalidClientError(detail: unknown) {
  if (!detail || typeof detail !== "object") {
    return false;
  }
  const payload = detail as Record<string, unknown>;
  return typeof payload.error === "string" && payload.error.trim() === "invalid_client";
}
